// S13 (06-10, Pau: «ordenador e iPad por igual»): la lista del equipo plegada a una columna de caras. En una
// pantalla estrecha la lista de 336 px se comía media ventana y la oficina quedaba en una tira que había que
// desplazar. Plegada, cada agente es su retrato con la insignia de su situación (la misma que en la lista) y
// en el mismo orden («lo que te toca», primero). Pulsar una cara abre su chat; la flecha de arriba la despliega.
import { PanelLeftOpen, Plus } from 'lucide-react';
import { useMemo } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import type { ContratadoDTO } from '../compartido/vocabulario';
import { AvatarSituacion } from './ListaEquipoS12';
import { quien, SUPERFICIE } from './estado';
import { aspectoDe, etiquetaSituacion, leToca } from './situacion';
import { useBandeja } from './useBandeja';
import { useOrdenEquipo } from './useOrdenEquipo';

export const RIEL_PX = 68;

export function RielEquipo({
  contratados,
  abierto,
  alAbrir,
  alContratar,
  alDesplegar,
  alResaltar,
}: {
  contratados: Record<string, ContratadoDTO>;
  abierto: string | null;
  alAbrir: (agentId: string) => void;
  alContratar: () => void;
  alDesplegar: () => void;
  alResaltar?: (agentId: string | null) => void;
}) {
  const { visibles } = useBandeja(contratados);
  const orden = useOrdenEquipo(visibles);
  // Lo que te toca SIEMPRE arriba (crítica S13: con el orden de la lista, los fijados que trabajan tranquilos iban
  // delante y quien te esperaba quedaba el 7.º, sin separador). Dentro de cada parte, el orden de la lista.
  const { toca, resto } = useMemo(() => {
    return { toca: orden.filter((c) => leToca(c)), resto: orden.filter((c) => !leToca(c)) };
  }, [orden]);
  const boton = 'inline-flex h-11 w-11 items-center justify-center rounded-o-m outline-none transition-colors focus-visible:ring-2 focus-visible:ring-oficina-enlace/70';
  return (
    <nav aria-label={t('oficinaV2.equipo.titulo')} className={cn('flex max-h-full flex-col items-center overflow-hidden rounded-o-l py-2', SUPERFICIE)} style={{ width: RIEL_PX }} data-testid="riel-equipo">
      <button type="button" onClick={alDesplegar} aria-label={t('oficinaV2.equipo.desplegar')} title={t('oficinaV2.equipo.desplegar')} className={cn(boton, 'text-oficina-texto2 hover:bg-oficina-hover hover:text-oficina-texto')}>
        <PanelLeftOpen className="h-5 w-5" aria-hidden />
      </button>
      <button type="button" onClick={alContratar} aria-label={t('oficinaV2.equipo.contratarAlguien')} title={t('oficinaV2.equipo.contratarAlguien')} className={cn(boton, 'mt-1 bg-oficina-acento text-oficina-acento-tinta hover:bg-oficina-acento-hover')}>
        <Plus className="h-5 w-5" aria-hidden />
      </button>
      {toca.length ? (
        <span
          role="status"
          className="mt-2 inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-oficina-acento px-2 text-o-sub font-semibold tabular-nums text-oficina-acento-tinta"
          title={t(toca.length === 1 ? 'oficinaV2.resumen.teNecesitaUno' : 'oficinaV2.resumen.teNecesitanVarios', { n: toca.length })}
          data-testid="riel-te-toca"
        >
          <span aria-hidden>{toca.length}</span>
          <span className="sr-only">{t(toca.length === 1 ? 'oficinaV2.resumen.teNecesitaUno' : 'oficinaV2.resumen.teNecesitanVarios', { n: toca.length })}</span>
        </span>
      ) : null}
      <div className="mt-2 w-full border-t border-white/[0.06]" />
      <ul className="flex min-h-0 w-full flex-col items-center gap-1.5 overflow-y-auto py-2 [scrollbar-width:thin]">
        {[...toca, ...resto].map((c, i) => {
          const activo = c.agentId === abierto;
          return (
            <li key={c.agentId} className={cn('relative', i === toca.length && toca.length > 0 && 'mt-1.5 border-t border-white/[0.08] pt-3')}>
              {/* Lo que te toca: una barra a la izquierda, como la fila teñida de la lista. */}
              {leToca(c) ? <span className={cn('absolute -left-2 top-1.5 h-8 border-l-[3px]', aspectoDe(c).borde)} aria-hidden /> : null}
              <button
                type="button"
                onClick={() => alAbrir(c.agentId)}
                onPointerEnter={(e) => e.pointerType === 'mouse' && alResaltar?.(c.agentId)}
                onPointerLeave={(e) => e.pointerType === 'mouse' && alResaltar?.(null)}
                aria-current={activo ? 'true' : undefined}
                aria-label={`${quien(c)}. ${etiquetaSituacion(c.situacion)}`}
                title={quien(c)}
                className={cn('rounded-o-m p-1 outline-none transition-colors focus-visible:ring-2 focus-visible:ring-oficina-enlace/70', activo ? 'bg-white/[0.12] ring-1 ring-inset ring-white/30' : 'hover:bg-white/[0.06]')}
              >
                <AvatarSituacion c={c} tamano={40} anillo={activo ? 'ring-[#29323B]' : 'ring-oficina-panel'} />
                {c.noLeido && !activo ? <span className="absolute right-0.5 top-0.5 h-2.5 w-2.5 rounded-full bg-oficina-texto ring-2 ring-oficina-panel" aria-hidden data-testid="riel-sin-leer" /> : null}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
